import React from 'react';
import {
	useDispatch,
	useSelector,
} from 'react-redux';

import useAllWebsites from '~/hooks/useAllWebsites';
import useHasWebsitesScreen from '~/hooks/useHasWebsitesScreen';

import {
	markAppEmptyState,
} from '~/actions';

import {
	EMPTY_STATE_NO_WEBSITES,
} from '~/model/ui/appEmpty';

import {
	navigate,
} from '~/routing/router';

import {
	lastSelectedWebsiteSelector,
} from '~/state/lastSelectedWebsite/selectors';

import getArrayItemAtSafeIndex from '~/utilities/getArrayItemAtSafeIndex';



/**
 * Returns a function that navigates to the "home destination" - the same logic
 * as post-login redirect (RedirectToHomepage):
 * - 4+ websites → multiDashboard (websites page)
 * - 1-3 websites → website.dashboard (first or last selected)
 * - 0 websites → empty_state
 *
 * Used when clicking Technical in the new navigation to replicate the same
 * behavior as the default post-login redirect.
 */
function useNavigateToHomeDestination() {
	const allWebsites = useAllWebsites();
	const dispatch = useDispatch();
	const hasWebsitesScreen = useHasWebsitesScreen();
	const lastSelectedWebsiteId = useSelector(lastSelectedWebsiteSelector);

	return React.useCallback(
		() => {
			if (allWebsites.isLoaded === false) {
				return;
			}

			if (hasWebsitesScreen) {
				navigate('multiDashboard', {});

				return;
			}

			if (
				lastSelectedWebsiteId
				&& allWebsites.listAll().find((website) => website.id === lastSelectedWebsiteId) !== undefined
			) {
				dispatch(markAppEmptyState(false));

				navigate('website.dashboard', {
					websiteId: lastSelectedWebsiteId,
				});

				return;
			}

			if (allWebsites.listAll().length > 0) {
				const websiteId = getArrayItemAtSafeIndex(allWebsites.listAll(), 0).id;

				dispatch(markAppEmptyState(false));

				navigate('website.dashboard', {
					websiteId,
				});

				return;
			}

			if (allWebsites.listAll().length === 0) {
				dispatch(markAppEmptyState(EMPTY_STATE_NO_WEBSITES));

				navigate('empty_state', {});
			}
		},
		[
			allWebsites,
			dispatch,
			hasWebsitesScreen,
			lastSelectedWebsiteId,
		],
	);
}



export default useNavigateToHomeDestination;
